import { useOutletContext } from "react-router";
import { Skeleton } from "primereact/skeleton";
import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils/themeClasses";

import type { UserData } from "@/pages/Users";

// ---------------------------------------------------------------------------
// Page-level constants (single consumer — not worth extracting)
// ---------------------------------------------------------------------------

const PROFILE_FIELD_LABELS = {
  EMAIL: "Email Address",
  FIRST_NAME: "First Name",
  LAST_NAME: "Last Name",
  COUNTRY: "Country",
  COMPANY: "Company",
  DEPARTMENT: "Department",
  ROLE: "Role",
  FUNCTION: "Function/Responsibilities",
  TITLE: "Title",
  TEAM_NAME: "Team Name",
  BUSINESS_UNIT: "Business Unit",
  EMPLOYEE_NUMBER: "Employee Number",
  SECURITY_CHAMPION: "Security Champion",
  EXPERIENCE: "Years of Experience",
  JOB_FUNCTION: "Job Function",
  EXPERTISE: "Technical Expertise",
  LANGUAGE: "Programming Language",
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatValue = (
  value: string | undefined,
  options: { label: string; value: string }[]
): string => {
  if (!value) return "N/A";
  return options.find(opt => opt.value === value)?.label ?? value;
};

// ---------------------------------------------------------------------------
// Sub-component
// ---------------------------------------------------------------------------

interface ProfileFieldProps {
  label: string;
  value: string | undefined;
}

function ProfileField({ label, value }: ProfileFieldProps) {
  return (
    <div>
      <p className={`my-2 ${themeClasses.textSurface700}`}>{label}</p>
      <p className="mt-1 font-medium">{value || "N/A"}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab component
// ---------------------------------------------------------------------------

export default function UserProfileTab() {
  const { user, isLoading } = useOutletContext<{ user: UserData | null; isLoading: boolean }>();

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-x-20 gap-y-4 my-8">
        {Array.from({ length: 4 }).map((_, si) => (
          <div key={si}>
            <div className="grid grid-cols-2 gap-10">
              {Array.from({ length: 4 }).map((_, fi) => (
                <div key={fi}>
                  <Skeleton width="100px" className="mb-2" />
                  <Skeleton width="200px" />
                </div>
              ))}
            </div>
            <hr className="my-12 border-gray-400" />
          </div>
        ))}
      </div>
    );
  }

  if (!user) {
    return (
      <div role="status" className="my-8">
        <p className="body-sm">User profile could not be loaded.</p>
      </div>
    );
  }

  const { attributes = {} } = user;
  const dp = user.developerProfile ?? {};

  const personalFields = [
    { label: PROFILE_FIELD_LABELS.EMAIL, value: user.email },
    { label: PROFILE_FIELD_LABELS.FIRST_NAME, value: user.name?.givenName },
    { label: PROFILE_FIELD_LABELS.LAST_NAME, value: user.name?.familyName },
    { label: PROFILE_FIELD_LABELS.COUNTRY, value: attributes.country },
  ];

  const workFields = [
    { label: PROFILE_FIELD_LABELS.COMPANY, value: attributes.company },
    { label: PROFILE_FIELD_LABELS.DEPARTMENT, value: attributes.department },
    { label: PROFILE_FIELD_LABELS.ROLE, value: attributes.jobRole },
    { label: PROFILE_FIELD_LABELS.TITLE, value: attributes.title },
  ];

  const teamFields = [
    { label: PROFILE_FIELD_LABELS.TEAM_NAME, value: attributes.teamName },
    { label: PROFILE_FIELD_LABELS.BUSINESS_UNIT, value: attributes.businessUnit },
    { label: PROFILE_FIELD_LABELS.EMPLOYEE_NUMBER, value: attributes.employeeNumber },
    {
      label: PROFILE_FIELD_LABELS.SECURITY_CHAMPION,
      value:
        attributes.securityChampion != null
          ? attributes.securityChampion
            ? "Yes"
            : "No"
          : undefined,
    },
  ];

  const aiUsageFields = DeveloperProfileConstants.OPTIONS.aiToolCategories.map(category => ({
    label: category.label,
    value: dp.aiStack?.[category.key]
      ?.map(v => category.options.find(o => o.value === v)?.label ?? v)
      .join(", "),
  }));
  // usesAi is only false when the user explicitly opted out; unset means unanswered
  const optedOutOfAi = dp.usesAi === false;

  const developerFields = [
    {
      label: PROFILE_FIELD_LABELS.EXPERIENCE,
      value: formatValue(dp.experience, DeveloperProfileConstants.OPTIONS.experienceLevels),
    },
    {
      label: PROFILE_FIELD_LABELS.JOB_FUNCTION,
      value: formatValue(dp.jobFunction, DeveloperProfileConstants.OPTIONS.jobRoles),
    },
    {
      label: PROFILE_FIELD_LABELS.EXPERTISE,
      value: formatValue(
        dp.expertise,
        DeveloperProfileConstants.OPTIONS.technicalExpertise as { label: string; value: string }[]
      ),
    },
    {
      label: PROFILE_FIELD_LABELS.LANGUAGE,
      value: formatValue(dp.language, DeveloperProfileConstants.OPTIONS.programmingLanguages),
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-x-20 gap-y-4 my-8">
      <div>
        <div className="grid grid-cols-2 gap-10">
          {personalFields.map(f => (
            <ProfileField key={f.label} label={f.label} value={f.value} />
          ))}
        </div>
        <hr className="my-12 border-gray-400" />
      </div>
      <div>
        <div className="grid grid-cols-2 gap-10">
          {workFields.map(f => (
            <ProfileField key={f.label} label={f.label} value={f.value} />
          ))}
        </div>
        <hr className="my-12 border-gray-400" />
      </div>
      <div>
        <div className="grid grid-cols-2 gap-10">
          {teamFields.map(f => (
            <ProfileField key={f.label} label={f.label} value={f.value} />
          ))}
        </div>
        <hr className="my-12 border-gray-400" />
      </div>
      <div>
        <div className="grid grid-cols-2 gap-10">
          {developerFields.map(f => (
            <ProfileField key={f.label} label={f.label} value={f.value} />
          ))}
        </div>
        <hr className="my-12 border-gray-400" />
      </div>
      <div>
        <h2 className="atlas-h5 mb-4">AI Usage</h2>
        {optedOutOfAi ? (
          <p className="mt-1 font-medium">I do not use AI</p>
        ) : (
          <div className="grid grid-cols-2 gap-10">
            {aiUsageFields.map(f => (
              <ProfileField key={f.label} label={f.label} value={f.value} />
            ))}
          </div>
        )}
        <hr className="my-12 border-gray-400" />
      </div>
    </div>
  );
}
